// /src/pages/TitleViewTablePage.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

export default function TitleViewTablePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  // Your App.js stores JWT under "token"
  const getToken = () => localStorage.getItem("token") || "";

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const token = getToken();

      const res = await fetch("/api/title-view", {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });

      const raw = await res.text();
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${raw.slice(0, 200)}`);

      let payload;
      try {
        payload = JSON.parse(raw);
      } catch {
        throw new Error(`Expected JSON but got: ${raw.slice(0, 200)}`);
      }

      setRows(Array.isArray(payload.items) ? payload.items : []);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // render empty string for null/undefined/empty
  const v = (x) => {
    if (x === null || x === undefined) return "";
    const s = String(x);
    return s.trim() === "" ? "" : s;
  };

  // map doc_type for display
  const displayType = (docTypeRaw) => {
    const dt = String(docTypeRaw || "").trim();
    if (!dt) return "";
    if (dt === "individual_vehicle") return "vehicle";
    if (dt === "scrap_inventory") return "TR9";
    return dt;
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((r) => {
      const haystacks = [
        r.source_filename, // hidden column, still searchable
        r.doc_id,
        r.id,
        displayType(r.doc_type),
        r.first_name,
        r.middle_name,
        r.last_name,
        r.seller_address, // hidden column, still searchable
        r.make,
        r.model,
        r.body_style,
        r.year,
        r.color,
        r.vin,
        r.title_number,
        r.stock_number,
        r.internal_number,
      ]
        .filter((x) => x !== null && x !== undefined && String(x).trim() !== "")
        .map((x) => String(x).toLowerCase());

      return haystacks.some((h) => h.includes(term));
    });
  }, [rows, q]);

  // unique window name per click (so each click creates a new tab)
  const makeTabName = () =>
    `aegis_pdf_${Date.now()}_${Math.random().toString(16).slice(2)}`;

  /**
   * Correct behavior:
   * - tab1 stays on Title View
   * - tab2 opens immediately (about:blank) on click
   * - when PDF is fetched, tab2 navigates to PDF
   *
   * IMPORTANT: do NOT use noopener/noreferrer features here because some browsers
   * will open the tab but return null, causing extra tabs and/or same-tab fallback.
   *
   * Also IMPORTANT: we NEVER navigate tab1 as a fallback.
   */
  const openPdf = async (row) => {
    if (!row?.pdf_url) return;

    setError("");

    const tabName = makeTabName();

    // 1) Open tab2 immediately (user gesture) and target it by name
    const w = window.open("about:blank", tabName);
    if (w) {
      try {
        w.document.title = "Loading PDF…";
        w.document.body.style.fontFamily =
          "system-ui, -apple-system, Segoe UI, Roboto, Arial";
        w.document.body.style.padding = "16px";
        w.document.body.innerHTML = "<div>Loading PDF…</div>";
      } catch {
        // ignore
      }
    } else {
      // Popup blocked outright
      setError(
        "Popup blocked. Please allow popups for this site so PDFs can open in a new tab."
      );
      return;
    }

    try {
      const token = getToken();
      const url = new URL(row.pdf_url, window.location.origin).toString();

      const res = await fetch(url, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/pdf",
        },
        credentials: "include",
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`PDF HTTP ${res.status}: ${text.slice(0, 200)}`);
      }

      const blob = await res.blob();

      // Guard against HTML responses (auth failure / misroute)
      if (blob.type && !blob.type.includes("pdf")) {
        const text = await blob.text().catch(() => "");
        throw new Error(
          `Expected PDF but got "${blob.type}". Body: ${text.slice(0, 200)}`
        );
      }

      const blobUrl = window.URL.createObjectURL(blob);

      // 2) Navigate tab2 (same named window). This should *reuse* the existing tab.
      const w2 = window.open(blobUrl, tabName);

      // If for some reason it can't target the window anymore, show a message in tab2.
      if (!w2) {
        try {
          w.document.body.innerHTML =
            "<div style='color:#b00020;white-space:pre-wrap;'>Could not navigate the new tab to the PDF (popup policy). Please allow popups.</div>";
        } catch {
          // ignore
        }
        setError("Could not navigate the new tab to the PDF. Please allow popups.");
        return;
      }

      // Cleanup later
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      const msg = String(e?.message || e);
      setError(msg);
      try {
        w.document.body.innerHTML = `<div style="color:#b00020;white-space:pre-wrap;">${msg}</div>`;
      } catch {
        // ignore
      }
    }
  };

  const TABLE_WRAP_STYLE = { fontSize: "12px" };
  const TH_STYLE = { fontWeight: 600, whiteSpace: "nowrap" };

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Link to="/titleagent" className="btn btn-outline-secondary btn-sm">
          ← Back
        </Link>
        <h1 className="h5 mb-0">Title View</h1>
        <div className="ms-auto d-flex gap-2">
          <button onClick={fetchAll} className="btn btn-outline-primary btn-sm">
            Refresh
          </button>
          <Link to="/titleagent" className="btn btn-outline-secondary btn-sm">
            Upload titles
          </Link>
        </div>
      </div>

      <div className="input-group mb-3">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="form-control form-control-sm"
          placeholder="Search doc id, VIN, names…"
        />
        <span className="input-group-text py-0" style={{ fontSize: "12px" }}>
          {filtered.length} {filtered.length === 1 ? "row" : "rows"}
        </span>
      </div>

      {loading && <div className="alert alert-secondary mb-3 py-2">Loading…</div>}
      {error && <div className="alert alert-danger mb-3 py-2">{error}</div>}
      {!loading && !error && filtered.length === 0 && (
        <div className="alert alert-warning py-2">No rows found.</div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="table-responsive" style={TABLE_WRAP_STYLE}>
          <table className="table table-sm table-striped table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ ...TH_STYLE, width: 70 }}>Open</th>
                <th style={TH_STYLE}>Type</th>
                <th style={TH_STYLE}>First</th>
                <th style={TH_STYLE}>Middle</th>
                <th style={TH_STYLE}>Last</th>
                <th style={TH_STYLE}>Make</th>
                <th style={TH_STYLE}>Year</th>
                <th style={TH_STYLE}>Model</th>
                <th style={TH_STYLE}>Body</th>
                <th style={TH_STYLE}>Color</th>
                <th style={TH_STYLE}>VIN</th>
                <th style={TH_STYLE}>Title #</th>
                <th style={TH_STYLE}>Stock #</th>
                <th style={TH_STYLE}>Internal #</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} style={{ cursor: "default" }}>
                  <td className="text-center">
                    {r.pdf_url ? (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm py-0 px-2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openPdf(r);
                        }}
                        title="Open PDF in a new tab"
                      >
                        Open
                      </button>
                    ) : (
                      <span className="text-muted" title="No PDF available"></span>
                    )}
                  </td>

                  <td>
                    <code style={{ fontSize: "12px" }}>
                      {v(displayType(r.doc_type))}
                    </code>
                  </td>
                  <td>{v(r.first_name)}</td>
                  <td>{v(r.middle_name)}</td>
                  <td>{v(r.last_name)}</td>
                  <td>{v(r.make)}</td>
                  <td>{v(r.year)}</td>
                  <td>{v(r.model)}</td>
                  <td>{v(r.body_style)}</td>
                  <td>{v(r.color)}</td>
                  <td className="text-nowrap">{v(r.vin)}</td>
                  <td>{v(r.title_number)}</td>
                  <td>{v(r.stock_number)}</td>
                  <td>{v(r.internal_number)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="text-muted small mt-2" style={{ fontSize: "12px" }}>
            Tip: PDFs open in a new tab in the browser viewer (print/download available there).
          </div>
        </div>
      )}
    </div>
  );
}

